import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { boutiquesAPI, consignmentsAPI, boutiquePaymentsAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Building2,
  DollarSign,
  Package,
  MapPin,
  Phone,
  Mail,
  Edit,
  Trash2,
  ChevronRight,
  ArrowLeft,
  CreditCard,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  RotateCcw,
  HelpCircle
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';
import { HelpTooltip } from '../components/ui/help-tooltip';
import { HelpButton } from '../components/HelpButton';

const PAYMENT_TERMS = {
  on_sale: 'Payment on Sale',
  net_15: 'Net 15 Days',
  net_30: 'Net 30 Days',
  net_60: 'Net 60 Days',
  monthly: 'Monthly Settlement',
};

export const Boutiques = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boutiques, setBoutiques] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  
  // Dialogs
  const [isBoutiqueDialogOpen, setIsBoutiqueDialogOpen] = useState(false);
  const [isConsignDialogOpen, setIsConsignDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingBoutique, setEditingBoutique] = useState(null);
  
  // Form data
  const [boutiqueForm, setBoutiqueForm] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    commission_rate: 40,
    payment_terms: 'net_30',
    notes: '',
    booth_rent: '',
    booth_number: '',
    booth_size: '',
  });
  
  const [consignForm, setConsignForm] = useState({
    product_id: '',
    quantity: 1,
    retail_price: '',
    consigned_date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    notes: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [boutiquesRes, productsRes] = await Promise.all([
        boutiquesAPI.getAll(),
        productsAPI.getAll(),
      ]);
      setBoutiques(boutiquesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoutiqueDetails = async (boutiqueId) => {
    try {
      const res = await boutiquesAPI.getOne(boutiqueId);
      setSelectedBoutique(res.data);
    } catch (error) {
      toast.error('Failed to load boutique details');
    }
  };

  const handleCreateBoutique = async () => {
    if (!boutiqueForm.name.trim()) {
      toast.error('Boutique name is required');
      return;
    }
    try {
      const payload = {
        ...boutiqueForm,
        booth_rent: boutiqueForm.booth_rent ? parseFloat(boutiqueForm.booth_rent) : null,
      };
      await boutiquesAPI.create(payload);
      toast.success('Boutique added');
      setIsBoutiqueDialogOpen(false);
      resetBoutiqueForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create boutique');
    }
  };

  const handleUpdateBoutique = async () => {
    if (!editingBoutique) return;
    try {
      const payload = {
        ...boutiqueForm,
        booth_rent: boutiqueForm.booth_rent ? parseFloat(boutiqueForm.booth_rent) : null,
      };
      await boutiquesAPI.update(editingBoutique.id, payload);
      toast.success('Boutique updated');
      setIsBoutiqueDialogOpen(false);
      setEditingBoutique(null);
      resetBoutiqueForm();
      fetchData();
      if (selectedBoutique?.id === editingBoutique.id) {
        fetchBoutiqueDetails(editingBoutique.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update boutique');
    }
  };

  const handleDeleteBoutique = async (boutiqueId) => {
    if (!window.confirm('Deactivate this boutique? You can reactivate it later.')) return;
    try {
      await boutiquesAPI.delete(boutiqueId);
      toast.success('Boutique deactivated');
      fetchData();
      if (selectedBoutique?.id === boutiqueId) {
        setSelectedBoutique(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete boutique');
    }
  };

  const handleConsign = async () => {
    if (!consignForm.product_id || !consignForm.quantity) {
      toast.error('Select a product and quantity');
      return;
    }
    
    // Get retail price from the selected product
    const selectedProduct = products.find(p => p.id === consignForm.product_id);
    const retailPrice = consignForm.retail_price || selectedProduct?.retail_price || 0;
    
    try {
      await consignmentsAPI.create({
        boutique_id: selectedBoutique.id,
        product_id: consignForm.product_id,
        quantity: parseInt(consignForm.quantity),
        retail_price: parseFloat(retailPrice),
        consigned_date: consignForm.consigned_date || new Date().toISOString().split('T')[0],
        notes: consignForm.notes,
      });
      toast.success('Items consigned');
      setIsConsignDialogOpen(false);
      setConsignForm({ product_id: '', quantity: 1, retail_price: '', consigned_date: new Date().toISOString().split('T')[0], notes: '' });
      fetchBoutiqueDetails(selectedBoutique.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to consign items');
    }
  };

  const handleReturn = async (consignmentId, quantity) => {
    try {
      await consignmentsAPI.returnItems(consignmentId, quantity);
      toast.success('Items returned');
      fetchBoutiqueDetails(selectedBoutique.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return items');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount) {
      toast.error('Payment amount is required');
      return;
    }
    try {
      await boutiquePaymentsAPI.create({
        boutique_id: selectedBoutique.id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes,
      });
      toast.success('Payment recorded');
      setIsPaymentDialogOpen(false);
      setPaymentForm({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        notes: '',
      });
      fetchBoutiqueDetails(selectedBoutique.id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openEditBoutique = (boutique) => {
    setEditingBoutique(boutique);
    setBoutiqueForm({
      name: boutique.name || '',
      contact_name: boutique.contact_name || '',
      email: boutique.email || '',
      phone: boutique.phone || '',
      address: boutique.address || '',
      city: boutique.city || '',
      state: boutique.state || '',
      commission_rate: boutique.commission_rate || 40,
      payment_terms: boutique.payment_terms || 'net_30',
      notes: boutique.notes || '',
      booth_rent: boutique.booth_rent?.toString() || '',
      booth_number: boutique.booth_number || '',
      booth_size: boutique.booth_size || '',
    });
    setIsBoutiqueDialogOpen(true);
  };

  const resetBoutiqueForm = () => {
    setBoutiqueForm({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      commission_rate: 40,
      payment_terms: 'net_30',
      notes: '',
      booth_rent: '',
      booth_number: '',
      booth_size: '',
    });
  };

  // Calculate totals
  const totalPending = boutiques.reduce((sum, b) => sum + (b.stats?.pending_payment || 0), 0);
  const totalConsigned = boutiques.reduce((sum, b) => sum + (b.stats?.items_consigned || 0), 0);
  const totalValue = boutiques.reduce((sum, b) => sum + (b.stats?.consignment_value || 0), 0);

  if (!user) return null;

  // Detail View
  if (selectedBoutique) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => setSelectedBoutique(null)}
            className="mb-4 text-[#1E3A5F]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Boutiques
          </Button>

          {/* Header */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-[#1E3A5F] font-['Outfit']">
                  {selectedBoutique.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-600">
                  {selectedBoutique.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {selectedBoutique.city}, {selectedBoutique.state}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    {selectedBoutique.commission_rate}% commission
                  </span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    {PAYMENT_TERMS[selectedBoutique.payment_terms]}
                  </span>
                </div>
                {/* Booth Details */}
                {(selectedBoutique.booth_number || selectedBoutique.booth_size || selectedBoutique.booth_rent) && (
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    {selectedBoutique.booth_number && (
                      <span>Booth #{selectedBoutique.booth_number}</span>
                    )}
                    {selectedBoutique.booth_size && (
                      <span>Size: {selectedBoutique.booth_size}</span>
                    )}
                    {selectedBoutique.booth_rent && (
                      <span>Rent: {formatCurrency(selectedBoutique.booth_rent)}/mo</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => openEditBoutique(selectedBoutique)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Items Consigned</p>
                <p className="text-2xl font-bold text-purple-600">
                  {selectedBoutique.consignments?.filter(c => c.status === 'active').reduce((s, c) => s + c.quantity, 0) || 0}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(selectedBoutique.sales?.reduce((s, sale) => s + sale.total_revenue, 0) || 0)}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Your Share</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency((selectedBoutique.sales?.reduce((s, sale) => s + sale.total_revenue, 0) || 0) * (1 - selectedBoutique.commission_rate / 100))}
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg">
                <p className="text-sm text-gray-600">Pending Payment</p>
                <p className="text-2xl font-bold text-amber-600">
                  {formatCurrency(
                    ((selectedBoutique.sales?.reduce((s, sale) => s + sale.total_revenue, 0) || 0) * (1 - selectedBoutique.commission_rate / 100)) -
                    (selectedBoutique.payments?.reduce((s, p) => s + p.amount, 0) || 0)
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mb-6">
            <Button onClick={() => setIsConsignDialogOpen(true)} className="btn-primary">
              <Package className="w-4 h-4 mr-2" />
              Consign Items
            </Button>
            <Button onClick={() => setIsPaymentDialogOpen(true)} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
              <CreditCard className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </div>

          {/* Consigned Items */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard mb-6">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Consigned Inventory
            </h2>
            {selectedBoutique.consignments?.filter(c => c.status === 'active').length > 0 ? (
              <div className="space-y-3">
                {selectedBoutique.consignments.filter(c => c.status === 'active').map((cons) => (
                  <div key={cons.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1E3A5F]">{cons.product?.name || 'Unknown Product'}</p>
                        <p className="text-sm text-gray-500">
                          Consigned: {formatDate(cons.consigned_date)} • Qty: {cons.quantity}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-semibold">{formatCurrency(cons.retail_value)}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReturn(cons.id, cons.quantity)}
                        className="text-amber-600 border-amber-300"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Return All
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No items currently consigned</p>
                <Button onClick={() => setIsConsignDialogOpen(true)} className="mt-4 btn-primary">
                  Consign Items
                </Button>
              </div>
            )}
          </div>

          {/* Payment History */}
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment History
            </h2>
            {selectedBoutique.payments?.length > 0 ? (
              <div className="space-y-2">
                {selectedBoutique.payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-700">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-gray-500">{formatDate(payment.payment_date)} • {payment.payment_method}</p>
                      </div>
                    </div>
                    {payment.notes && <p className="text-sm text-gray-500">{payment.notes}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500">No payments recorded yet</p>
            )}
          </div>
        </div>

        {/* Consign Dialog */}
        <Dialog open={isConsignDialogOpen} onOpenChange={setIsConsignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Consign Items to {selectedBoutique.name}</DialogTitle>
              <DialogDescription>Add products to consignment at this boutique</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Product</Label>
                <Select value={consignForm.product_id} onValueChange={(v) => {
                  const product = products.find(p => p.id === v);
                  setConsignForm({
                    ...consignForm, 
                    product_id: v,
                    retail_price: product?.retail_price?.toString() || ''
                  });
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} - {formatCurrency(p.retail_price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={consignForm.quantity}
                    onChange={(e) => setConsignForm({...consignForm, quantity: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Retail Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={consignForm.retail_price}
                    onChange={(e) => setConsignForm({...consignForm, retail_price: e.target.value})}
                    placeholder="Per item"
                  />
                </div>
              </div>
              <div>
                <Label>Consign Date</Label>
                <Input
                  type="date"
                  value={consignForm.consigned_date}
                  onChange={(e) => setConsignForm({...consignForm, consigned_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={consignForm.notes}
                  onChange={(e) => setConsignForm({...consignForm, notes: e.target.value})}
                  placeholder="e.g., Shelf location"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsConsignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleConsign} className="btn-primary">Consign Items</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment from {selectedBoutique.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="ach">ACH/Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="e.g., Check #1234"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRecordPayment} className="bg-green-600 hover:bg-green-700 text-white">Record Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit'] flex items-center gap-2">
                Boutique Partners
                <HelpTooltip content="Track products you've consigned to local shops and boutiques. Monitor sales, commissions, and payments." />
              </h1>
              <p className="text-gray-600 mt-1">Manage consignment relationships and track payments</p>
            </div>
            <HelpButton tutorialId="boutiques" />
          </div>
          <Button 
            onClick={() => { setEditingBoutique(null); resetBoutiqueForm(); setIsBoutiqueDialogOpen(true); }}
            className="btn-primary"
            data-testid="add-boutique-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Boutique
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Boutiques</p>
                <p className="text-2xl font-bold text-[#1E3A5F]">{boutiques.filter(b => b.is_active !== false).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Items Consigned</p>
                <p className="text-2xl font-bold text-[#1E3A5F]">{totalConsigned} ({formatCurrency(totalValue)})</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Payments</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalPending)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Boutiques List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : boutiques.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No boutique partners yet</h3>
            <p className="text-gray-600 mb-4">Add your first boutique to start tracking consignments</p>
            <Button onClick={() => setIsBoutiqueDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Boutique
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boutiques.map((boutique) => (
              <div
                key={boutique.id}
                className={`bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard hover:shadow-lg transition-shadow cursor-pointer ${!boutique.is_active ? 'opacity-60' : ''}`}
                onClick={() => fetchBoutiqueDetails(boutique.id)}
                data-testid={`boutique-${boutique.id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Building2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F]">{boutique.name}</h3>
                      {boutique.city && (
                        <p className="text-xs text-gray-500">{boutique.city}, {boutique.state}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Commission</p>
                    <p className="font-semibold text-[#1E3A5F]">{boutique.commission_rate}%</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Items</p>
                    <p className="font-semibold text-[#1E3A5F]">{boutique.stats?.items_consigned || 0}</p>
                  </div>
                </div>
                
                {boutique.stats?.pending_payment > 0 && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{formatCurrency(boutique.stats.pending_payment)} pending</span>
                  </div>
                )}
                
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditBoutique(boutique)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    onClick={() => handleDeleteBoutique(boutique.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Boutique Dialog */}
      <Dialog open={isBoutiqueDialogOpen} onOpenChange={setIsBoutiqueDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingBoutique ? 'Edit Boutique' : 'Add Boutique Partner'}</DialogTitle>
            <DialogDescription>
              {editingBoutique ? 'Update boutique details' : 'Add a new boutique or consignment shop'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Boutique Name *</Label>
              <Input
                value={boutiqueForm.name}
                onChange={(e) => setBoutiqueForm({...boutiqueForm, name: e.target.value})}
                placeholder="e.g., The Craft Corner"
                data-testid="boutique-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={boutiqueForm.contact_name}
                  onChange={(e) => setBoutiqueForm({...boutiqueForm, contact_name: e.target.value})}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={boutiqueForm.phone}
                  onChange={(e) => setBoutiqueForm({...boutiqueForm, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={boutiqueForm.email}
                onChange={(e) => setBoutiqueForm({...boutiqueForm, email: e.target.value})}
                placeholder="contact@boutique.com"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>City</Label>
                <Input
                  value={boutiqueForm.city}
                  onChange={(e) => setBoutiqueForm({...boutiqueForm, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={boutiqueForm.state}
                  onChange={(e) => setBoutiqueForm({...boutiqueForm, state: e.target.value})}
                  placeholder="VA"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  Commission Rate (%)
                  <HelpTooltip content="The percentage the boutique keeps from each sale. Typical rates are 30-50%." />
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={boutiqueForm.commission_rate}
                  onChange={(e) => setBoutiqueForm({...boutiqueForm, commission_rate: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  Payment Terms
                  <HelpTooltip content="How often the boutique pays you for sold items." />
                </Label>
                <Select
                  value={boutiqueForm.payment_terms}
                  onValueChange={(v) => setBoutiqueForm({...boutiqueForm, payment_terms: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYMENT_TERMS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Booth Details Section */}
            <div className="pt-4 border-t border-gray-200">
              <Label className="text-sm font-semibold text-[#1E3A5F] mb-3 block">Booth Details (Optional)</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    Booth Rent ($)
                    <HelpTooltip content="Monthly or periodic rent for your booth/shelf space at this boutique." />
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={boutiqueForm.booth_rent}
                    onChange={(e) => setBoutiqueForm({...boutiqueForm, booth_rent: e.target.value})}
                    placeholder="0.00"
                    data-testid="boutique-booth-rent"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    Booth Number
                    <HelpTooltip content="Your assigned booth or display number at this location." />
                  </Label>
                  <Input
                    value={boutiqueForm.booth_number}
                    onChange={(e) => setBoutiqueForm({...boutiqueForm, booth_number: e.target.value})}
                    placeholder="e.g., A12"
                    data-testid="boutique-booth-number"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    Booth Size
                    <HelpTooltip content="The size of your booth or shelf space." />
                  </Label>
                  <Input
                    value={boutiqueForm.booth_size}
                    onChange={(e) => setBoutiqueForm({...boutiqueForm, booth_size: e.target.value})}
                    placeholder="e.g., 4x6 ft"
                    data-testid="boutique-booth-size"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={boutiqueForm.notes}
                onChange={(e) => setBoutiqueForm({...boutiqueForm, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsBoutiqueDialogOpen(false); setEditingBoutique(null); resetBoutiqueForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={editingBoutique ? handleUpdateBoutique : handleCreateBoutique}
              className="btn-primary"
              data-testid="save-boutique-btn"
            >
              {editingBoutique ? 'Update Boutique' : 'Add Boutique'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Boutiques;
